import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import api from "../../lib/api";
import useReveal from "../../hooks/useReveal";
import useSEO from "../../seo/useSEO";
import { breadcrumb, SITE_URL } from "../../seo/schemas";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";

  useReveal();
  useSEO({
    title: "Blog — Oguzhan Ozcelik | Engineering notes",
    description:
      "Engineering notes from Oguzhan Ozcelik on Shopify, React, Node.js, performance and SEO.",
    path: "/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          "@id": `${SITE_URL}/blog#blog`,
          url: `${SITE_URL}/blog`,
          name: "Oguzhan Ozcelik — Blog",
          description:
            "Engineering notes on Shopify, React, Node.js, performance and SEO.",
          author: { "@id": `${SITE_URL}/#person` },
          inLanguage: "en-US",
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ]),
      ],
    },
    jsonLdId: "blog-jsonld",
  });

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = activeCategory ? { params: { category: activeCategory } } : undefined;
    api
      .get("/api/posts", params)
      .then(({ data }) => { if (live) setPosts(data.posts || []); })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [activeCategory]);

  useEffect(() => {
    let live = true;
    api
      .get("/api/posts/categories")
      .then(({ data }) => { if (live) setCategories(data.categories || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const filterChips = useMemo(() => {
    const set = new Set(categories);
    if (activeCategory) set.add(activeCategory);
    return Array.from(set);
  }, [categories, activeCategory]);

  const onPickCategory = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (cat) next.set("category", cat);
    else next.delete("category");
    setSearchParams(next, { replace: true });
  };

  return (
    <main>
      <section className="section section--hero">
        <div className="container-x reveal">
          <span className="eyebrow">Notebook</span>
          <h1 className="about-hero__title">
            <span className="stroke">Engineering</span>
            <br />
            <span className="it text-accent">notes & rants.</span>
          </h1>
          <p className="lead" style={{ maxWidth: "60ch", marginTop: 24 }}>
            Practical posts on Shopify, React, Node.js, Laravel, performance
            and the messy middle of shipping software.
          </p>
        </div>
      </section>

      <section className="section section--tight">
        <div className="container-x">
          {filterChips.length > 0 && (
            <div className="filter-row reveal" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 28 }}>
              <button
                type="button"
                className="chip"
                onClick={() => onPickCategory("")}
                style={{
                  cursor: "pointer",
                  background: !activeCategory ? "var(--accent)" : "transparent",
                  color: !activeCategory ? "#0a0a0a" : "var(--text-soft)",
                  borderColor: !activeCategory ? "var(--accent)" : "var(--border-strong)",
                }}
              >
                All
              </button>
              {filterChips.map((c) => (
                <button
                  type="button"
                  key={c}
                  className="chip"
                  onClick={() => onPickCategory(c)}
                  style={{
                    cursor: "pointer",
                    background: activeCategory === c ? "var(--accent)" : "transparent",
                    color: activeCategory === c ? "#0a0a0a" : "var(--text-soft)",
                    borderColor: activeCategory === c ? "var(--accent)" : "var(--border-strong)",
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {loading && <p className="text-muted">Loading posts…</p>}
          {error && <p style={{ color: "var(--danger)" }}>Failed to load posts: {error}</p>}
          {!loading && !error && posts.length === 0 && (
            <div className="testimonial reveal">
              <p className="testimonial__quote">
                {activeCategory
                  ? `No published posts in ${activeCategory} yet — try another category.`
                  : "No published posts yet — drafts are brewing. Check back soon."}
              </p>
            </div>
          )}

          <div className="work-grid">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="work reveal"
                aria-label={p.title}
              >
                <div className="work__media" style={{ aspectRatio: "16/9" }}>
                  <span className="work__badge">
                    <span className="dot" /> {p.category || p.tags?.[0] || "Engineering"}
                  </span>
                  <span className="work__arrow"><HiArrowUpRight /></span>
                  {p.coverImage ? (
                    <img src={p.coverImage} alt={p.title} loading="lazy" />
                  ) : (
                    <div
                      style={{
                        width: "100%", height: "100%",
                        display: "grid", placeItems: "center",
                        background:
                          "linear-gradient(135deg, rgba(216,255,60,0.08), rgba(255,184,74,0.04))",
                        color: "var(--muted)",
                        fontFamily: "var(--font-display)", fontSize: 14, letterSpacing: "0.16em",
                        textTransform: "uppercase",
                      }}
                    >
                      {p.tags?.slice(0, 2).join(" · ") || "Notes"}
                    </div>
                  )}
                </div>
                <div className="work__body" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <span className="work__cat">
                      {fmtDate(p.publishedAt)} · {p.readingMinutes || 5} min read
                    </span>
                  </div>
                  <span className="work__title" style={{ marginTop: 6 }}>{p.title}</span>
                  {p.excerpt && (
                    <p style={{ margin: "8px 0 0", color: "var(--muted)", fontSize: 14 }}>
                      {p.excerpt}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
